import { useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { readDeviceToken } from "../lib/device"

export function SettingsPage() {
  const navigate = useNavigate()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [isClearing, setIsClearing] = useState(false)

  async function clearData() {
    const deviceToken = readDeviceToken()

    if (!deviceToken) {
      toast.info("Nothing to clear")
      setConfirmOpen(false)
      return
    }

    setIsClearing(true)

    try {
      const response = await fetch("/api/me/delete", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ deviceToken }),
      })

      if (response.status === 429) {
        toast.error("Too many delete requests. Try again in an hour.")
      } else if (response.status === 400) {
        toast.error("Invalid token format")
      } else {
        toast.success("Your data has been cleared")
      }
    } finally {
      localStorage.clear()
      setIsClearing(false)
      setConfirmOpen(false)
      navigate("/")
    }
  }

  return (
    <main
      id="main"
      className="mx-auto flex min-h-dvh w-full max-w-md flex-col bg-background text-foreground"
    >
      <header className="border-b-2 border-border px-5 py-5">
        <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-muted-foreground">
          Ledger · Settings
        </p>
        <h1 className="mt-2 font-mono text-2xl font-bold uppercase tracking-tight text-foreground">
          Privacy
        </h1>
      </header>

      <section className="px-5 py-6">
        <div
          className="border-2 border-border bg-card p-5"
          style={{ boxShadow: "var(--shadow-brutal)" }}
        >
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
            Device record
          </p>
          <ul className="mt-3 flex flex-col gap-3 text-sm">
            <li className="flex gap-2">
              <span className="font-mono text-muted-foreground">01 ·</span>
              <span>Scores and history are tied to this device.</span>
            </li>
            <li className="flex gap-2">
              <span className="font-mono text-muted-foreground">02 ·</span>
              <span>
                Room history and event logs are retained for 90 days and then automatically
                deleted.
              </span>
            </li>
            <li className="flex gap-2">
              <span className="font-mono text-muted-foreground">03 ·</span>
              <span>Playing again on this device creates a new identity.</span>
            </li>
          </ul>
        </div>
      </section>

      <div className="mt-auto flex flex-col gap-2 px-5 py-5">
        <Button
          className="h-14 bg-primary text-primary-foreground hover:bg-primary"
          onClick={() => setConfirmOpen(true)}
        >
          Clear my data ↗
        </Button>
        <Button
          asChild
          variant="outline"
          className="h-12 bg-transparent text-foreground hover:bg-card"
        >
          <Link to="/">Return home</Link>
        </Button>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear my data?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete your scores and history. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isClearing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={clearData}
              disabled={isClearing}
              className="bg-primary text-primary-foreground hover:bg-primary"
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  )
}
